import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Cloud as CloudIcon,
  Router as RouterIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  Public as PublicIcon,
  Speed as SpeedIcon,
  Storage as StorageIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const CloudEdgeSystemDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/pi-planning');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to PI Planning Board"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Cloud and Edge Hybrid Infrastructure
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Distributed Computing Architecture for Border Operations
          </Typography>
        </Box>
      </Box>

      {/* System Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            System Overview
          </Typography>
          <Typography variant="body1" paragraph>
            CBP's Cloud and Edge Hybrid Infrastructure represents a dual computing approach that enables sensitive data 
            processing at the border (edge) while maintaining enterprise systems in secure cloud environments. This 
            architecture balances the need for low-latency, real-time decision-making at ports of entry with the scalability, 
            reliability, and advanced analytics capabilities of cloud computing, ensuring operational continuity and data 
            sovereignty.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="Cloud Edge System 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is Cloud Edge Infrastructure"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    A <strong>hybrid computing architecture</strong> combining cloud-based enterprise systems with edge computing 
                    devices deployed at ports of entry. Cloud infrastructure hosts <strong>ACE, TECS, analytics platforms</strong> 
                    in AWS GovCloud, while edge devices provide <strong>real-time processing, AI inference, and offline capability</strong> 
                    for mission-critical border operations requiring sub-second response times.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who manages Cloud Edge Infrastructure"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Managed by <strong>CBP Office of Information and Technology (OIT)</strong> with cloud operations in partnership 
                    with <strong>AWS GovCloud</strong>. Edge infrastructure deployed and maintained by <strong>field IT teams</strong> 
                    at ports of entry. Used by <strong>60,000+ CBP personnel</strong> including officers, agents, analysts, and 
                    support staff across all operational environments.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was Cloud Edge deployed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Cloud migration began in <strong>2017</strong> with initial AWS GovCloud deployment. <strong>ACE cloud 
                    transition (2019-2021)</strong>, edge computing pilots (2020), and <strong>hybrid architecture implementation 
                    (2022-present)</strong>. Ongoing modernization with <strong>5G edge connectivity</strong> and expanded edge AI 
                    capabilities. Operates <strong>24/7/365</strong> with 99.99% availability SLA.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is Cloud Edge deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>Cloud infrastructure</strong> hosted in AWS GovCloud (US-East, US-West) with disaster recovery sites. 
                    <strong> Edge devices deployed at 328 ports of entry</strong> including land borders, airports, and seaports. 
                    <strong> Border Patrol stations</strong> with edge computing for remote operations. <strong>National Targeting 
                    Center</strong> and operational command centers with hybrid access to both environments.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why Cloud Edge exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To balance <strong>cloud scalability and analytics</strong> with <strong>edge low-latency and data sovereignty</strong>. 
                    Enables <strong>real-time border decisions</strong> without cloud dependency while leveraging cloud for enterprise 
                    systems, big data analytics, and AI training. Ensures <strong>operational continuity</strong> during network outages 
                    and maintains <strong>sensitive data at the border</strong> per security and privacy requirements.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Architecture Components */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <CloudIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Hybrid Architecture Components
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CloudIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Cloud Infrastructure
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Scalable, secure cloud platform for enterprise systems and analytics.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Enterprise Systems:</strong> ACE, TECS, IDENT hosted in AWS GovCloud</Typography></li>
                <li><Typography variant="body2"><strong>Data Lake:</strong> Centralized storage for analytics and ML training</Typography></li>
                <li><Typography variant="body2"><strong>Microservices:</strong> Containerized applications on Kubernetes</Typography></li>
                <li><Typography variant="body2"><strong>Auto-Scaling:</strong> Dynamic resource allocation based on demand</Typography></li>
                <li><Typography variant="body2"><strong>Disaster Recovery:</strong> Multi-region redundancy and backup</Typography></li>
                <li><Typography variant="body2"><strong>API Gateway:</strong> Secure access for edge and external systems</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <RouterIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Edge Computing
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Border-based processing for low-latency, real-time operations.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Edge Devices:</strong> Ruggedized servers at inspection booths and checkpoints</Typography></li>
                <li><Typography variant="body2"><strong>Local Processing:</strong> Biometric matching, document scanning without cloud</Typography></li>
                <li><Typography variant="body2"><strong>AI Inference:</strong> Real-time ML model execution at the border</Typography></li>
                <li><Typography variant="body2"><strong>Offline Capability:</strong> Critical functions operate without connectivity</Typography></li>
                <li><Typography variant="body2"><strong>Data Caching:</strong> Local storage of frequently accessed records</Typography></li>
                <li><Typography variant="body2"><strong>Sync Mechanisms:</strong> Automated data synchronization when connected</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Sensitive Data Isolation
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Privacy-preserving architecture for classified and sensitive information.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Data Sovereignty:</strong> Sensitive data remains at border edge devices</Typography></li>
                <li><Typography variant="body2"><strong>Encryption:</strong> End-to-end encryption for data in transit and at rest</Typography></li>
                <li><Typography variant="body2"><strong>Access Controls:</strong> Role-based permissions and multi-factor authentication</Typography></li>
                <li><Typography variant="body2"><strong>Network Segmentation:</strong> Isolated networks for classified systems</Typography></li>
                <li><Typography variant="body2"><strong>Data Minimization:</strong> Only necessary data sent to cloud</Typography></li>
                <li><Typography variant="body2"><strong>Audit Logging:</strong> Comprehensive tracking of data access and movement</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SyncIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                  Hybrid Orchestration
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Seamless coordination between cloud and edge environments.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Workload Distribution:</strong> Intelligent routing of tasks to optimal location</Typography></li>
                <li><Typography variant="body2"><strong>Data Synchronization:</strong> Bidirectional sync with conflict resolution</Typography></li>
                <li><Typography variant="body2"><strong>Model Deployment:</strong> Automated ML model updates to edge devices</Typography></li>
                <li><Typography variant="body2"><strong>Monitoring:</strong> Unified observability across cloud and edge</Typography></li>
                <li><Typography variant="body2"><strong>Failover:</strong> Automatic switching between cloud and edge processing</Typography></li>
                <li><Typography variant="body2"><strong>Configuration Management:</strong> Centralized control of edge device settings</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Technical Specifications */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Technical Specifications
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Component</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cloud</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Edge</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>Compute</strong></TableCell>
              <TableCell>AWS EC2, ECS, Lambda (serverless)</TableCell>
              <TableCell>Intel Xeon servers, NVIDIA Jetson (AI)</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Storage</strong></TableCell>
              <TableCell>S3 (object), EBS (block), RDS (database)</TableCell>
              <TableCell>Local SSD/NVMe, limited capacity</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Networking</strong></TableCell>
              <TableCell>VPC, Direct Connect, Transit Gateway</TableCell>
              <TableCell>5G/LTE, fiber, satellite backup</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Latency</strong></TableCell>
              <TableCell>50-200ms (varies by location)</TableCell>
              <TableCell>&lt;10ms (local processing)</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Availability</strong></TableCell>
              <TableCell>99.99% (multi-AZ deployment)</TableCell>
              <TableCell>99.9% (local redundancy)</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Security</strong></TableCell>
              <TableCell>FedRAMP High, FIPS 140-2</TableCell>
              <TableCell>Physical security, encryption</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Scalability</strong></TableCell>
              <TableCell>Virtually unlimited (auto-scaling)</TableCell>
              <TableCell>Fixed capacity per device</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Use Cases</strong></TableCell>
              <TableCell>Analytics, training, enterprise apps</TableCell>
              <TableCell>Real-time decisions, offline ops</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* System Scale */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Infrastructure Scale
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                1000+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Edge Devices
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Deployed at ports
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                10+ PB
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cloud Storage
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                AWS GovCloud capacity
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                60K+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Users Supported
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                CBP personnel
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                99.99%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Availability SLA
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Hybrid architecture
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Hybrid Architecture Benefits
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Low Latency:</strong> Edge processing enables sub-second response times</Typography></li>
                <li><Typography variant="body2"><strong>Offline Capability:</strong> Critical operations continue without connectivity</Typography></li>
                <li><Typography variant="body2"><strong>Data Sovereignty:</strong> Sensitive data remains at the border</Typography></li>
                <li><Typography variant="body2"><strong>Scalability:</strong> Cloud provides unlimited capacity for analytics</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Cost Optimization:</strong> Right-sized infrastructure for each workload</Typography></li>
                <li><Typography variant="body2"><strong>Resilience:</strong> Redundancy across cloud and edge environments</Typography></li>
                <li><Typography variant="body2"><strong>Innovation:</strong> Cloud enables rapid deployment of new capabilities</Typography></li>
                <li><Typography variant="body2"><strong>Compliance:</strong> Meets federal security and privacy requirements</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Technology Stack */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Technology Stack
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Chip label="AWS GovCloud" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Cloud platform</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Kubernetes" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Container orchestration</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="NVIDIA Jetson" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Edge AI devices</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="5G/LTE" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Edge connectivity</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Apache Kafka" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Event streaming</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Terraform" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Infrastructure as code</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Prometheus/Grafana" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Monitoring and observability</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="HashiCorp Vault" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Secrets management</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default CloudEdgeSystemDetail;
